import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import isEqual from 'deep-eql';

import { CategoryPageSideFilters } from '@c/CategoryOrSubcategoryPage';
import { MdFilterList } from "react-icons/md";

import { MasterDocument } from '@util/firestore/master';
import { CategoryDocument } from '@util/firestore/categories';
import { BrandDocument } from '@util/types/firestore/brand';
import { ConditionDocument } from '@util/types/firestore/conditions';
import MultiSelectComboBox from '@ui/MultiSelectComboBox';
import { getConditionShortLabel } from '@util/conditions/conditionShortLabel';
import PriceRangeFilterSection from './sections/PriceRangeFilterSection';
import PopoverBox from '@ui/PopoverBox';
import { findADealOptions, useCaseOptions } from '@util/maps/filters';
import { getCategoryFilterMode } from '@util/filters';

export interface FilterItem {
  key: string;
  label: string;
  selected?: boolean;
}

interface CategoryFilterProps {
  conditionOptions: ConditionDocument[];
  brandOptions: BrandDocument[];
  categoryOptions: CategoryDocument[] | null;
  initialFilters: CategoryPageSideFilters;
  masterProduct?: MasterDocument;
  hideBrands?: boolean;
  hideCats?: boolean;
  onFiltersChange: (filters: CategoryPageSideFilters) => void;  
  onOpenDrawer?: () => void;
}

const ListingFilters = ({
  categoryOptions,
  initialFilters,
  conditionOptions,
  brandOptions,
  hideBrands = false,
  hideCats = false,
  onFiltersChange,
  onOpenDrawer
}: CategoryFilterProps) => {
  const router = useRouter();
  // Use asPath (actual URL) instead of pathname (route pattern)
  // Split on ? to ignore query params when checking route
  const path = router.asPath.split('?')[0].toLowerCase();
  const disableDealsFilterRoutes = [
    '/collections/featured',
    '/collections/deals-and-steals',
    '/collections/auctions',
  ];

  const [filterState, setFilterState] = useState<CategoryPageSideFilters>(initialFilters);
  const { showUseCase, showFindADeal } = getCategoryFilterMode(filterState.category);

  const [showAllBrands, setShowAllBrands] = useState(false);
  const [pricePopoverCloseSignal, setPricePopoverCloseSignal] = useState<number>(0);

  useEffect(() => {
    if (!isEqual(initialFilters, filterState)) {
      setFilterState(initialFilters);
    }
  }, [initialFilters]);

  const handleFilterChange = (newFilters: CategoryPageSideFilters) => {
    setFilterState(newFilters);
    onFiltersChange(newFilters);
  };

  return (
    <div className="flex flex-wrap gap-[1rem] w-full justify-center sm:justify-start text-center sm:text-left">
      {/* Filter button */}
      {onOpenDrawer && (
        <div
          onClick={onOpenDrawer}
          className="
              flex items-center justify-center
              w-[115px] h-[42.5px] py-2 rounded-[10px]
              bg-brand-lightest-gray hover:bg-brand-lighter-gray
              cursor-pointer
            "
        >
          <MdFilterList className="text-[24px]" />
          <div>All Filters</div>
        </div>
      )}

      {/* Category dropdown */}
      {!hideCats && (
        <div className="flex-grow sm:flex-grow-0 sm:w-[75%] md:max-w-[200px]">
          <MultiSelectComboBox
            title={
              filterState.category?.parents?.[0]
                ? categoryOptions?.find(cat => cat.id === filterState.category?.parents?.[0])?.name
                : filterState.category?.name || undefined
            }
            options={
              categoryOptions
                ? (() => {
                  const selected = filterState.category;

                  if (selected && selected.level === 1) {
                    // Show children of selected Level 1 category
                    return categoryOptions
                      .filter((cat) => cat.parents?.includes(selected.id))
                      .map((cat) => ({
                        id: cat.id,
                        value: cat.id,
                        label: cat.name,
                      }));
                  }

                  if (selected && selected.level > 1 && Array.isArray(selected.parents) && selected.parents.length > 0) {
                    const parentId = selected.parents[0];
                    return categoryOptions.filter(
                      (cat) => Array.isArray(cat.parents) && cat.parents[0] === parentId
                    ).map((cat) => ({
                      id: cat.id,
                      value: cat.id,
                      label: cat.name,
                    }));
                  }

                  return categoryOptions
                    .filter((cat) => cat.level === 1)
                    .map((cat) => ({
                      id: cat.id,
                      value: cat.id, // <--- Important
                      label: cat.name,
                    }));
                })()
                : []
            }
            placeholder={filterState.category ? filterState.category.name : "Categories"}
            showAllOptions={false}
            allOptionLabel={"All Categories"}
            allOptionIcon="chevron"
            value={filterState.category ? [filterState.category.id] : []}
            onChange={(selected) => {
              console.log("onChange", selected)
              if (selected.length === 0) {
                const updated = {
                  ...filterState,
                  category: null
                };
                handleFilterChange(updated);
              } else {
                const selectedCategory = categoryOptions?.find(
                  (cat) => cat.id === selected[selected.length - 1].id
                ) || null;

                const updated = {
                  ...filterState,
                  category: selectedCategory
                };
                handleFilterChange(updated);
              }
            }}
            dropdownBoxSize="250px"
          />
        </div>
      )}

      {!hideBrands && (
        <div className="w-[200px] hidden md:block">
          <MultiSelectComboBox
            onShowMore={() => {setShowAllBrands(!showAllBrands)}}
            isShowMore={showAllBrands}
            options={
              (showAllBrands ? brandOptions : brandOptions.filter(b =>
                ['Arri', 'Canon', 'Fujifilm', 'Leica', 'Nikon', 'Panasonic', 'Red Digital Cinema', 'Sony'].includes(b.name)
              )).map((b) => ({ id: b.id, value: b.name, label: b.name }))
            }
            placeholder="Brands"
            allOptionLabel="All Brands"
            value={filterState.brand}
            onChange={(selected) => {
              if (selected.length === 0) {
                const updated = {
                  ...filterState,
                  brand: []
                };
                handleFilterChange(updated);
              } else {
                const updated = {
                  ...filterState,
                  brand: selected.map((s) => s.value)
                };
                handleFilterChange(updated);
              }
            }}
          />
        </div>
      )}

      <div className="w-[200px] hidden md:block">
        <MultiSelectComboBox
          placeholder="Condition"
          allOptionLabel="All Conditions"
          options={
            conditionOptions.map((c) => ({ id: c.name, value: c.name, label: getConditionShortLabel(c.name) }))
          }
          value={filterState.condition?.filter((v): v is string => !!v) ?? []}
          onChange={(selected) => {
            const values = selected.map((s) => s.value);

            if (values.includes('__all__')) {
              const updated = {
                ...filterState,
                condition: []
              };
              handleFilterChange(updated);
            } else {
              const updated = {
                ...filterState,
                condition: values
              };
              handleFilterChange(updated);
            }
          }}
          dropdownBoxSize="220px"
        />
      </div>

      <div className="w-[200px] hidden md:block">
        <PopoverBox 
          key={pricePopoverCloseSignal}
          placeholder={
            filterState.minPrice !== null && filterState.maxPrice === null
              ? `Over $${filterState.minPrice}`
              : filterState.maxPrice !== null && filterState.minPrice === null
                ? `Under $${filterState.maxPrice}`
                : filterState.minPrice !== null && filterState.maxPrice !== null
                  ? `$${filterState.minPrice} - $${filterState.maxPrice}`
                  : "Price"
          }

          closeSignal={pricePopoverCloseSignal}
        >
          <PriceRangeFilterSection
            title="Price"
            onChange={(min, max) => {
              const updated = {
                ...filterState,
                minPrice: min,
                maxPrice: max,
              };
              handleFilterChange(updated);
            }}
            onSearch={() => {
              setPricePopoverCloseSignal(Date.now());
            }}
            initialMin={filterState.minPrice || null}
            initialMax={filterState.maxPrice || null}
            showIcon={true}
          />
        </PopoverBox>
      </div>

      <div className="w-[200px] hidden md:block">
        {showUseCase ? (
          <MultiSelectComboBox
            placeholder="Use Case"
            options={useCaseOptions}
            value={filterState.useCases || []}
            onChange={(selected) => {
              const useCases = selected.map((s) => s.value);
              const updated = { ...filterState, useCases };
              handleFilterChange(updated);
            }}
          />
        ) : showFindADeal ? (
          <MultiSelectComboBox
            placeholder="Find a Deal"
            options={findADealOptions}
            value={filterState.deals}
            isDisabled={disableDealsFilterRoutes.includes(path)}
            onChange={(selected) => {
              const deals = selected.map((s) => s.value);
              const updated = { ...filterState, deals };
              handleFilterChange(updated);
            }}
          />
        ) : null}
      </div>

    </div>
  )
};

export default ListingFilters;
